import React, { useEffect, useState } from 'react';
import { ClockIcon, CheckIcon, MessageSquareIcon, CalendarIcon, CreditCardIcon, GlobeIcon } from 'lucide-react';
import { detectUserCurrency, convertPrice, currencies } from '../utils/currency';
import { trackConversion } from '../utils/analytics';
export function PackagesSection() {
  const [selectedCurrency, setSelectedCurrency] = useState('EUR');
  const [isLoadingCurrency, setIsLoadingCurrency] = useState(true);
  useEffect(() => {
    detectUserCurrency().then(currency => {
      setSelectedCurrency(currency);
      setIsLoadingCurrency(false);
    });
  }, []);
  // Base prices in EUR
  const packages = [{
    name: 'Basic IVF Coordination',
    subtitle: 'Native IVF + ICSI',
    duration: '3-4 Days',
    ideal: 'Perfect for couples seeking affordable, comprehensive care',
    priceEur: 3200,
    features: ['Native IVF cycle with ICSI (clinic treatment)', 'Translator for all communication', 'Fast appointment scheduling', 'Document & medical form preparation', 'Travel-timing optimization for short time away from work', 'WhatsApp support (12 hours/day)', 'Preparation for tests (STD, ultrasound, etc.) in your home country', 'Post-treatment follow-up scheduling'],
    color: 'from-blue-600 to-blue-700',
    popular: false
  }, {
    name: 'Full IVF Cycle Management',
    subtitle: 'Stimulation Cycle',
    duration: '5-6 Days',
    ideal: 'Most popular - Full treatment with enhanced support',
    priceEur: 4300,
    features: ['Full IVF cycle (with stimulation plan, retrieval, transfer)', 'Translator for all consultations, online calls, and clinic interactions', 'Full-cycle calendar creation to minimize travel window', 'Medication instruction support', 'Pharmacy guidance', 'Daily reminders during treatment', 'Appointment scheduling for blood tests, scans, retrieval', '24/7 support during treatment days', 'Aftercare planning and next-step guidance', 'Coordination of any required tests in your home country'],
    color: 'from-cyan-600 to-cyan-700',
    popular: true
  }, {
    name: 'Donor IVF Management',
    subtitle: 'Egg or Embryo Donation',
    duration: '6-7 Days',
    ideal: 'Premium donor-assisted treatment with full support',
    priceEur: 5000,
    priceNoteEur: 'Egg Donation: €7,200 | Donated Embryo: €5,000',
    eggDonationPriceEur: 7200,
    features: ['Donor egg cycle or donor embryo transfer', 'Translator for all communications', 'Donor matching explanation & logistics', 'Step-by-step timing plan (shortest possible stay)', 'All appointment scheduling', 'Medication & instructions support', 'Daily treatment check-ins', 'Complete admin management with the clinic', 'Aftercare & follow-up coordination', 'Support for sperm freezing (if needed)'],
    color: 'from-blue-500 to-blue-600',
    popular: false
  }];
  return <section id="packages" className="py-20 bg-gradient-to-b from-slate-50 to-white" data-id="element-451">
      <div className="container mx-auto px-4" data-id="element-452">
        <div className="text-center mb-16" data-id="element-453">
          <div className="inline-block bg-blue-50 rounded-full px-4 py-2 mb-4" data-id="element-454">
            <span className="text-blue-700 font-bold text-sm tracking-wider uppercase" data-id="element-455">
              Treatment Packages
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-456">
            Complete{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600" data-id="element-457">
              Coordination Packages
            </span>
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-lg font-light leading-relaxed mb-6" data-id="element-458">
            Fixed-price coordination packages with everything you need - medical
            treatment at partner clinics, translator services, and full support
            throughout your journey.
          </p>
          {/* Currency Selector */}
          <div className="flex items-center justify-center gap-3" data-id="element-459">
            <GlobeIcon className="text-slate-400" size={20} data-id="element-460" />
            <select value={selectedCurrency} onChange={e => setSelectedCurrency(e.target.value)} className="bg-white border border-slate-200 rounded-full px-4 py-2 text-slate-700 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500" disabled={isLoadingCurrency} data-id="element-461">
              {Object.entries(currencies).map(([code, config]) => <option key={code} value={code} data-id="element-462">
                  {config.symbol} {code}
                </option>)}
            </select>
            <span className="text-sm text-slate-500" data-id="element-463">
              {isLoadingCurrency ? 'Detecting...' : 'Change currency'}
            </span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto" data-id="element-464">
          {packages.map((pkg, index) => <div key={index} className={`relative bg-white border ${pkg.popular ? 'border-blue-400 scale-105 shadow-xl' : 'border-slate-200'} rounded-3xl p-8 hover:shadow-xl transition-all duration-300`} data-id="element-465">
              {pkg.popular && <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-4 py-1 rounded-full text-sm font-bold tracking-wide" data-id="element-466">
                  Most Popular
                </div>}
              <div className="text-center mb-6" data-id="element-467">
                <div className={`inline-flex items-center gap-2 bg-gradient-to-br ${pkg.color} p-4 rounded-2xl mb-4`} data-id="element-468">
                  <ClockIcon className="text-white" size={28} data-id="element-469" />
                </div>
                <h3 className="text-2xl font-black text-slate-900 mb-1 tracking-tight" data-id="element-470">
                  {pkg.name}
                </h3>
                <p className="text-blue-600 font-semibold mb-2 text-sm" data-id="element-471">
                  {pkg.subtitle}
                </p>
                <p className="text-slate-600 text-sm font-light mb-4" data-id="element-472">
                  {pkg.ideal}
                </p>
                <div className="text-3xl font-black text-slate-900 mb-1 tracking-tight" data-id="element-473">
                  {convertPrice(pkg.priceEur, selectedCurrency)}
                </div>
                {pkg.priceNoteEur && <p className="text-slate-500 text-xs font-medium mb-2" data-id="element-474">
                    Egg Donation:{' '}
                    {convertPrice(pkg.eggDonationPriceEur!, selectedCurrency)} |
                    Donated Embryo:{' '}
                    {convertPrice(pkg.priceEur, selectedCurrency)}
                  </p>}
                <p className="text-slate-600 text-sm font-light" data-id="element-475">
                  complete package
                </p>
              </div>
              <div className="space-y-3 mb-8" data-id="element-476">
                {pkg.features.map((feature, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-477">
                    <div className={`bg-gradient-to-br ${pkg.color} p-1 rounded-full mt-0.5 flex-shrink-0`} data-id="element-478">
                      <CheckIcon className="text-white" size={14} data-id="element-479" />
                    </div>
                    <span className="text-slate-600 text-sm" data-id="element-480">{feature}</span>
                  </div>)}
              </div>
              <a href="/contact" onClick={() => {
            // Track package selection as conversion
            trackConversion('PACKAGE_CLICK', pkg.priceEur, selectedCurrency);
          }} className={`block w-full text-center bg-gradient-to-r ${pkg.color} text-white py-4 rounded-xl font-bold hover:opacity-90 transition-all duration-300 shadow-lg tracking-wide`} data-id="element-481">
                Select Package
              </a>
            </div>)}
        </div>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-482">
          <div className="bg-white border border-slate-100 p-6 rounded-2xl shadow-sm" data-id="element-483">
            <div className="flex items-center gap-4 mb-3" data-id="element-484">
              <div className="bg-blue-600 p-3 rounded-xl" data-id="element-485">
                <MessageSquareIcon className="text-white" size={24} data-id="element-486" />
              </div>
              <h4 className="text-slate-900 font-bold text-lg" data-id="element-487">
                Expert Translation Services
              </h4>
            </div>
            <p className="text-slate-600 text-sm" data-id="element-488">
              Professional translator included for all clinic communications,
              ensuring clear understanding throughout your treatment journey.
            </p>
          </div>
          <div className="bg-white border border-slate-100 p-6 rounded-2xl shadow-sm" data-id="element-489">
            <div className="flex items-center gap-4 mb-3" data-id="element-490">
              <div className="bg-cyan-600 p-3 rounded-xl" data-id="element-491">
                <CalendarIcon className="text-white" size={24} data-id="element-492" />
              </div>
              <h4 className="text-slate-900 font-bold text-lg" data-id="element-493">
                Complete Coordination
              </h4>
            </div>
            <p className="text-slate-600 text-sm" data-id="element-494">
              Full appointment scheduling, document preparation, and treatment
              timeline management to minimize your time away from work.
            </p>
          </div>
        </div>
        <div className="mt-20 max-w-5xl mx-auto" data-id="element-495">
          <div className="text-center mb-12" data-id="element-496">
            <div className="inline-block bg-blue-50 rounded-full px-4 py-2 mb-4" data-id="element-497">
              <span className="text-blue-700 font-bold text-sm tracking-wider uppercase" data-id="element-498">
                Flexible Payment
              </span>
            </div>
            <h3 className="text-3xl md:text-4xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-499">
              Simple{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600" data-id="element-500">
                Payment Plan
              </span>
            </h3>
            <p className="text-slate-600 text-lg font-light" data-id="element-501">
              Spread your costs across three manageable payments
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-502">
            <div className="bg-white border-2 border-blue-200 rounded-3xl p-8 hover:shadow-xl transition-all duration-300" data-id="element-503">
              <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-4 rounded-2xl inline-block mb-6" data-id="element-504">
                <CreditCardIcon className="text-white" size={28} data-id="element-505" />
              </div>
              <div className="mb-4" data-id="element-506">
                <div className="text-4xl font-black text-blue-600 mb-2" data-id="element-507">
                  30%
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-3" data-id="element-508">
                  Initial Deposit
                </h4>
              </div>
              <p className="text-slate-600 leading-relaxed" data-id="element-509">
                Reserves your appointment, begins travel planning, and secures
                translator time for your treatment.
              </p>
            </div>
            <div className="bg-white border-2 border-cyan-200 rounded-3xl p-8 hover:shadow-xl transition-all duration-300" data-id="element-510">
              <div className="bg-gradient-to-br from-cyan-600 to-cyan-700 p-4 rounded-2xl inline-block mb-6" data-id="element-511">
                <CalendarIcon className="text-white" size={28} data-id="element-512" />
              </div>
              <div className="mb-4" data-id="element-513">
                <div className="text-4xl font-black text-cyan-600 mb-2" data-id="element-514">
                  50%
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-3" data-id="element-515">
                  Before Travel
                </h4>
              </div>
              <p className="text-slate-600 leading-relaxed" data-id="element-516">
                Covers coordination services and finalizes clinic scheduling for
                your treatment dates.
              </p>
            </div>
            <div className="bg-white border-2 border-blue-200 rounded-3xl p-8 hover:shadow-xl transition-all duration-300" data-id="element-517">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 rounded-2xl inline-block mb-6" data-id="element-518">
                <CheckIcon className="text-white" size={28} data-id="element-519" />
              </div>
              <div className="mb-4" data-id="element-520">
                <div className="text-4xl font-black text-blue-600 mb-2" data-id="element-521">
                  20%
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-3" data-id="element-522">
                  Final Payment
                </h4>
              </div>
              <p className="text-slate-600 leading-relaxed" data-id="element-523">
                Due 7 days before travel. Completes your package and ensures all
                arrangements are confirmed.
              </p>
            </div>
          </div>
        </div>
        <div className="mt-12 text-center" data-id="element-524">
          <p className="text-slate-600 mb-4" data-id="element-525">
            All packages include clinic treatment, translator services, and full
            coordination support.
          </p>
          <a href="/contact" className="inline-block bg-white border-2 border-blue-600 text-blue-600 px-8 py-3 rounded-full hover:bg-blue-50 transition-all duration-300 font-semibold" data-id="element-526">
            Discuss Your Needs
          </a>
        </div>
      </div>
    </section>;
}